import {
  AlertCircle,
  Check,
  Edit,
  Loader2,
  Plus,
  Settings,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/ui/button';
import { ConfirmDialog } from '@/ui/confirm-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/ui/tooltip';
import { formatDate, formatDateTime } from './profile-center-api-key-utils';
import { ProfileCenterApiKeyCreateModal } from './profile-center-api-key-create-modal';
import { ProfileCenterApiKeyEditModal } from './profile-center-api-key-edit-modal';
import { useProfileCenterApiKeyPanel } from './use-profile-center-api-key-panel';

export function ProfileCenterApiKeyPanel() {
  const {
    apiKeys,
    loading,
    error,
    fetchApiKeys,
    isCreateModalOpen,
    newKeyName,
    setNewKeyName,
    newKeyExpireAt,
    setNewKeyExpireAt,
    creating,
    createdKey,
    copied,
    handleOpenCreateModal,
    handleCloseCreateModal,
    handleCreate,
    handleCopy,
    isEditModalOpen,
    editingKey,
    editName,
    setEditName,
    updating,
    handleOpenEditModal,
    handleCloseEditModal,
    handleUpdate,
    isToggleStatusDialogOpen,
    setIsToggleStatusDialogOpen,
    togglingKey,
    toggling,
    handleOpenToggleStatusDialog,
    handleCloseToggleStatusDialog,
    handleConfirmToggleStatus,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    deletingKey,
    deleting,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog,
    handleDelete,
  } = useProfileCenterApiKeyPanel();

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6 rounded-lg border border-blue-100 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <Settings className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" />
          <div className="text-sm text-blue-800">
            <p className="mb-1">API Key 用于 Agent 访问平台接口的认证凭证。</p>
            <p>
              列表内展示的是脱敏后的 Key，完整的 Key
              仅在创建时显示一次，请妥善保存。不要与他人共享你的 API Key。
            </p>
          </div>
        </div>
      </div>

      <div className="mb-4 flex items-center">
        <Button
          onClick={handleOpenCreateModal}
          className="gap-2 rounded-lg px-4 py-2 text-white shadow-sm"
          style={{
            background: 'linear-gradient(135deg, #7bb8ff 0%, #3F3663 100%)',
          }}
        >
          <Plus className="h-4 w-4" />
          创建 API Key
        </Button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
          <div className="flex items-center">
            <AlertCircle className="mr-2 h-4 w-4 text-red-500" />
            <span className="text-sm text-red-700">{error}</span>
            <button
              onClick={fetchApiKeys}
              className="ml-2 text-sm text-blue-500 hover:text-blue-600"
            >
              重试
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-[920px] w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-[140px] whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                名称
              </th>
              <th className="min-w-[360px] whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Key
              </th>
              <th className="w-[120px] whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                创建日期
              </th>
              <th className="w-[120px] whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                到期时间
              </th>
              <th className="w-[80px] whitespace-nowrap px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                状态
              </th>
              <th className="w-[100px] whitespace-nowrap px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                    <span className="text-sm text-gray-500">加载中...</span>
                  </div>
                </td>
              </tr>
            ) : apiKeys.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-8 text-center text-sm text-gray-500"
                >
                  暂无 API Key，点击"创建 API Key"按钮创建
                </td>
              </tr>
            ) : (
              apiKeys.map((key) => {
                const keyDisplay = key.key_display || key.key_preview || '-';
                const expiresAt = key.expires_at || key.expire_at;
                const isActive = key.status === 'active';
                const displayName = key.name || '-';

                return (
                  <tr key={key.id} className="hover:bg-gray-50">
                    <td className="max-w-[160px] px-4 py-4 text-sm font-medium text-gray-900">
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="block truncate">
                              {displayName}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs break-words">
                            {displayName}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 font-mono text-sm text-gray-600">
                      {keyDisplay}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                      {formatDate(key.created_at)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                      {formatDate(expiresAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-center">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {isActive ? '启用' : '禁用'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() =>
                                  handleOpenToggleStatusDialog(key)
                                }
                                className={`rounded p-1.5 transition-colors ${
                                  isActive
                                    ? 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                                    : 'text-gray-400 hover:bg-green-50 hover:text-green-500'
                                }`}
                                aria-label={isActive ? '禁用' : '启用'}
                              >
                                {isActive ? (
                                  <X className="h-4 w-4" />
                                ) : (
                                  <Check className="h-4 w-4" />
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {isActive ? '禁用' : '启用'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => handleOpenEditModal(key)}
                                className="rounded p-1.5 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                                aria-label="编辑"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>编辑</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => handleOpenDeleteDialog(key)}
                                className="rounded p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                aria-label="删除"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>删除</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ProfileCenterApiKeyCreateModal
        isOpen={isCreateModalOpen}
        creating={creating}
        newKeyName={newKeyName}
        setNewKeyName={setNewKeyName}
        newKeyExpireAt={newKeyExpireAt}
        setNewKeyExpireAt={setNewKeyExpireAt}
        createdKey={createdKey}
        copied={copied}
        onClose={handleCloseCreateModal}
        onCreate={handleCreate}
        onCopy={handleCopy}
        formatDateTime={formatDateTime}
      />

      <ProfileCenterApiKeyEditModal
        isOpen={isEditModalOpen}
        editingKey={editingKey}
        editName={editName}
        setEditName={setEditName}
        updating={updating}
        onClose={handleCloseEditModal}
        onUpdate={handleUpdate}
      />

      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsDeleteDialogOpen(open);
          if (!open) {
            handleCloseDeleteDialog();
          }
        }}
        title="确认删除"
        description={
          deletingKey ? (
            <span>
              确定要删除 API Key <strong>{deletingKey.name}</strong> 吗？
              <br />
              删除后将无法恢复，使用此 Key 的 Agent 将无法访问平台接口。
            </span>
          ) : (
            ''
          )
        }
        confirmText="删除"
        cancelText="取消"
        onConfirm={handleDelete}
        variant="destructive"
        loading={deleting}
        zIndex="z-[1001]"
      />

      <ConfirmDialog
        open={isToggleStatusDialogOpen}
        onOpenChange={(open) => {
          setIsToggleStatusDialogOpen(open);
          if (!open) {
            handleCloseToggleStatusDialog();
          }
        }}
        title={togglingKey?.status === 'active' ? '确认禁用' : '确认启用'}
        description={
          togglingKey ? (
            <span>
              确定要{togglingKey.status === 'active' ? '禁用' : '启用'} API Key{' '}
              <strong>{togglingKey.name}</strong> 吗？
            </span>
          ) : (
            ''
          )
        }
        confirmText={togglingKey?.status === 'active' ? '禁用' : '启用'}
        cancelText="取消"
        onConfirm={handleConfirmToggleStatus}
        variant={togglingKey?.status === 'active' ? 'destructive' : 'default'}
        loading={toggling}
        zIndex="z-[1001]"
      />
    </div>
  );
}
